import { useEffect, useRef, useState } from 'react';
import { ArrowRight, ArrowLeft } from '../../assets/svgImages';
import { Product, GuestSimilarProductsProps } from '../../interface/interface';
import { calculateOutOfStockEnum } from '../../utils/outOfStockStatus';

import { useGuestCart } from '../../context/guestCartContext';
import GuestProductCards from './GuestProductCards';

const GuestSimilarProducts = ({ products }: GuestSimilarProductsProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const {
    getProductQuantity,
    addToCartMutation,
    removeProductFromCartMutation,
  } = useGuestCart();

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollButtons = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  useEffect(() => {
    updateScrollButtons();

    const el = scrollRef.current;
    if (!el) return;

    el.addEventListener('scroll', updateScrollButtons);
    window.addEventListener('resize', updateScrollButtons);

    return () => {
      el.removeEventListener('scroll', updateScrollButtons);
      window.removeEventListener('resize', updateScrollButtons);
    };
  }, [products]);

  const scroll = (direction: 'left' | 'right') => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollBy({
      left: direction === 'left' ? -240 : 240,
      behavior: 'smooth',
    });
  };

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadingState =
      loading ||
      addToCartMutation.isPending ||
      removeProductFromCartMutation.isPending;

    if (!loadingState) {
      setLoading(false);
    } else {
      setLoading(true);
    }
  }, [
    loading,
    addToCartMutation.isPending,
    removeProductFromCartMutation.isPending,
  ]);

  function frameProductObject(product: Product) {
    const orderedQuantity = getProductQuantity(product?.product_code) ?? 0;

    if (product?.substituteProduct) {
      const subProduct = product?.substituteProduct;
      const subOrderedQuantity =
        getProductQuantity(subProduct?.product_code) ?? 0;

      return {
        ...product,
        substituteProduct: {
          ...subProduct,
          productMaxQty: product?.productMaxQty ?? product.inventory_quantity,
          orderedQuantity: subOrderedQuantity,
          outOfStock: calculateOutOfStockEnum(subProduct, orderedQuantity),
        },
        productMaxQty: product?.productMaxQty ?? product.inventory_quantity,
        orderedQuantity,
        outOfStock: calculateOutOfStockEnum(product, orderedQuantity),
      };
    }

    return {
      ...product,
      orderedQuantity,
      outOfStock: calculateOutOfStockEnum(product, orderedQuantity),
    };
  }

  if (!products?.length) return null;

  return (
    <div>
      <h2 className="text-lg md:text-xl xl:text-2xl font-semibold text-primary-blue-500 mb-4 lg:mb-5">
        You may also like
      </h2>
      <div className="relative">
        <div
          ref={scrollRef}
          className="flex gap-4 overflow-x-auto snap-x snap-mandatory scroll-smooth scrollbar-hide"
        >
          {products.map((product) => (
            <div
              key={product.id}
              className="shrink-0 snap-start basis-[calc(50%-8px)] sm-land:basis-[calc(33.333%-10.667px)] lg:basis-[calc(25%-12px)]"
            >
              <GuestProductCards
                product={frameProductObject(product)}
                addProductToCart={async (id: string) => {
                  await addToCartMutation.mutateAsync(id);
                }}
                removeProductFromCart={async (productId: string) => {
                  await removeProductFromCartMutation.mutateAsync(productId);
                }}
                isAccessories={false}
                allowedProduct={false}
                isGuest={true}
              />
            </div>
          ))}
        </div>

        <button
          type="button"
          onClick={() => scroll('left')}
          disabled={!canScrollLeft}
          className={`flex absolute left-1 sm:-left-4 lg:-left-6 top-1/2 -translate-y-1/2 bg-white border border-gray-200
          rounded-full w-8 h-8 items-center justify-center shadow transition-colors z-10
          ${canScrollLeft ? 'hover:bg-gray-50 cursor-pointer' : 'opacity-40 cursor-not-allowed'}`}
          aria-label="Scroll left"
          aria-disabled={!canScrollLeft}
        >
          <ArrowRight />
        </button>
        <button
          type="button"
          onClick={() => scroll('right')}
          disabled={!canScrollRight}
          className={`flex absolute right-1 sm:-right-4 lg:-right-6 top-1/2 -translate-y-1/2 bg-white border border-gray-200
          rounded-full w-8 h-8 items-center justify-center shadow transition-colors z-10
          ${canScrollRight ? 'hover:bg-gray-50 cursor-pointer' : 'opacity-40 cursor-not-allowed'}`}
          aria-label="Scroll right"
          aria-disabled={!canScrollRight}
        >
          <ArrowLeft />
        </button>
      </div>
    </div>
  );
};

export default GuestSimilarProducts;
